/* eslint-disable import/no-anonymous-default-export */
import _ from "lodash";
import { database, firebase } from "../firebase";
import { v4 as uuid } from "uuid";
import { Base64 } from 'js-base64';
import { ActivateCode } from "./User/indexv2";

const getUserId = () => {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) return undefined;
  return currentUser.uid;
};

const onAuthStateChanged = (onChange) => {
  const unsubscribe = firebase
    .auth()
    .onAuthStateChanged((user) => onChange(user));
  return () => unsubscribe();
};

const logout = () => {
  firebase.auth().signOut();
};

const login = (email, password) =>
  new Promise((resolve, reject) => {
    firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(
        () => resolve(),
        (error) => {
          let notification = "";
          switch (error.code) {
            case "auth/user-not-found":
              notification = "El correo no está registrado.";
              break;
            case "auth/wrong-password":
              notification = "La contraseña es incorrecta.";
              break;
            default:
              notification = `Ocurrió un error (${error.code} ${error.message}).`;
              break;
          }
          reject(notification);
        }
      );
  });

const register = (email, password, name, lastname, phone) =>
  new Promise(async (resolve, reject) => {

    let randUUID = uuid();
    let eid = _.kebabCase(email) + `-${randUUID.slice(0, 5)}`;
    let updates = {};
    updates[`/reg_users_attempt/${eid}/init`] = true;
    updates[`/reg_users_attempt/${eid}/init_payload`] = Base64.encode(JSON.stringify({ email, password, name, lastname, phone }));
    await database.ref().update(updates);

    firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(({ user }) => {

        const shortUId = user.uid.slice(0,5);
        const _name = typeof name === 'string' && name.length > 0 ? name.slice(0,20) :`${shortUId}`;
        const _lastname = typeof lastname === 'string' && lastname.length > 0 ? lastname.slice(0,20) : `${shortUId}`;
        const _phone = typeof phone === 'string' && phone.length > 0 ? phone.slice(0,9) :`999000999`;
        const _email = typeof email === 'string' && email.length > 0 ? email.slice(0,20) :`${shortUId}`;

        let updates = {};
        updates[`/users/${user.uid}/name`] = _name;
        updates[`/users/${user.uid}/lastname`] = _lastname;
        updates[`/users/${user.uid}/phone`] = _phone;
        updates[`/users/${user.uid}/email`] = _email;
        updates[`/users/${user.uid}/createdAt`] = firebase.database.ServerValue.TIMESTAMP;
        updates[`/users_temp/${user.uid}`] = true;
        updates[`/reg_users_attempt/${eid}/success`] = true;

        database.ref().update(updates, (error) => {
          if(error !== null){
            reject(`Ocurrió un error. Utiliza otro correo e intenta nuevamente. (${error.code} ${error.message})`);
          } else {
            resolve();
          }
        });
      })
      .catch(async (error) => {
        let notification = "defaultError";
        switch (error?.code) {
          case "auth/email-already-in-use":
            notification = "El correo ya está en uso.";
            break;
          case "auth/invalid-email":
            notification = "El correo es inválido.";
            break;

          case "auth/weak-password":
            notification = "La contraseña no es suficientemente segura.";
            break;
          default:
            notification = `Ocurrió un error (${error?.code || ""} ${error?.message || ""}).`;
            break;
        }
        let updates = {};
        updates[`/reg_users_attempt/${eid}/error`] = true;
        updates[`/reg_users_attempt/${eid}/error_payload`] = notification;
        await database.ref().update(updates);
        reject(notification);
      });
  });

const sendPasswordReset = (email) =>
  new Promise((resolve, reject) => {
    firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => resolve())
      .catch((error) => {
        let notification = "";
        switch (error.code) {
          case "auth/invalid-email":
            notification = "El correo es inválido.";
            break;
          case "auth/user-not-found":
            notification = "El correo no está registrado.";
            break;
          default:
            notification = `Ocurrió un error (${error.code} ${error.message}).`;
            break;
        }
        reject(notification);
      });
  });

const subscribeToCompletedEpisodes = (
  onReceive,
  onDelete,
  userId = getUserId()
) => {
  if (!userId) return () => {};

  const ref = database.ref(`completed/${userId}/episodes`);

  const handler = (snapshot) =>
    snapshot.val() === true ? onReceive(snapshot.key) : onDelete(snapshot.key);

  const handlerOnDelete = (snapshot) => onDelete(snapshot.key);

  ref.on("child_added", handler);
  ref.on("child_changed", handler);
  ref.on("child_removed", handlerOnDelete);

  return () => {
    ref.off("child_added", handler);
    ref.off("child_changed", handler);
    ref.off("child_removed", handlerOnDelete);
  };
};

const subscribeToPurchases = (onReceive, onDelete, userId = getUserId()) => {
  if (!userId) return () => {};

  const ref = database.ref(`purchases/${userId}`);

  const handler = (snapshot) =>
    snapshot.val() === true ? onReceive(snapshot.key) : onDelete(snapshot.key);

  const handlerOnDelete = (snapshot) => onDelete(snapshot.key);

  ref.on("child_added", handler);
  ref.on("child_changed", handler);
  ref.on("child_removed", handlerOnDelete);

  return () => {
    ref.off("child_added", handler);
    ref.off("child_changed", handler);
    ref.off("child_removed", handlerOnDelete);
  };
};

const subscribeToCompletedChapters = (
  episodeId,
  onReceive,
  onDelete,
  userId = getUserId()
) => {
  if (!userId) return () => {};

  const ref = database.ref(`completed/${userId}/chapters/${episodeId}`);

  const handler = (snapshot) =>
    snapshot.val() === true ? onReceive(snapshot.key) : onDelete(snapshot.key);

  const handlerOnDelete = (snapshot) => onDelete(snapshot.key);

  ref.on("child_added", handler);
  ref.on("child_changed", handler);
  ref.on("child_removed", handlerOnDelete);

  return () => {
    ref.off("child_added", handler);
    ref.off("child_changed", handler);
    ref.off("child_removed", handlerOnDelete);
  };
};

const subscribeToCompletedLessons = (
  chapterId,
  onReceive,
  onDelete,
  userId = getUserId()
) => {
  if (!userId) return () => {};

  const ref = database.ref(`completed/${userId}/lessons/${chapterId}`);

  const handler = (snapshot) =>
    snapshot.val() === true ? onReceive(snapshot.key) : onDelete(snapshot.key);

  const handlerOnDelete = (snapshot) => onDelete(snapshot.key);

  ref.on("child_added", handler);
  ref.on("child_changed", handler);
  ref.on("child_removed", handlerOnDelete);

  return () => {
    ref.off("child_added", handler);
    ref.off("child_changed", handler);
    ref.off("child_removed", handlerOnDelete);
  };
};

const subscribeToAttemptDetails = (
  lessonId,
  onReceive,
  onDelete,
  userId = getUserId()
) => {
  if (!userId) return () => {};

  const ref = database.ref(`attempts/${userId}/${lessonId}`);

  const handler = (snapshot) => onReceive(snapshot.key, snapshot.val());

  const handlerOnDelete = (snapshot) => onDelete(snapshot.key);

  ref.on("child_added", handler);
  ref.on("child_changed", handler);
  ref.on("child_removed", handlerOnDelete);

  return () => {
    ref.off("child_added", handler);
    ref.off("child_changed", handler);
    ref.off("child_removed", handlerOnDelete);
  };
};

const sendAttempt = (lessonId, { state, seed, data }) =>
  new Promise((resolve, reject) => {
    const userId = getUserId();
    if (!userId) {
      reject("No has iniciado sesión.");
      return;
    }

    const ref = database.ref(`attempts/${userId}/${lessonId}`);

    ref.transaction(
      (current) => {
        current = {
          attempt: current ? ++current.attempt : 1,
          state,
          seed,
          data,
        };
        return current;
      },
      (error, committed) =>
        error || !committed ? reject("Ocurrió un error.") : resolve()
    );
  });

const makeLessonCompleted = (lessonId, chapterId) =>
  new Promise((resolve, reject) => {
    const userId = getUserId();
    if (!userId) {
      reject("No has iniciado sesión.");
      return;
    }

    const ref = database.ref(
      `completed/${userId}/lessons/${chapterId}/${lessonId}`
    );

    ref
      .set(true)
      .then(() => resolve())
      .catch(() => reject("Ocurrió un error."));
  });

const makeChapterCompleted = (chapterId, episodeId) =>
  new Promise((resolve, reject) => {
    const userId = getUserId();
    if (!userId) {
      reject("No has iniciado sesión.");
      return;
    }

    const ref = database.ref(
      `completed/${userId}/chapters/${episodeId}/${chapterId}`
    );

    ref
      .set(true)
      .then(() => resolve())
      .catch(() => reject("Ocurrió un error."));
  });

const makeEpisodeCompleted = (episodeId) =>
  new Promise((resolve, reject) => {
    const userId = getUserId();
    if (!userId) {
      reject("No has iniciado sesión.");
      return;
    }

    const ref = database.ref(`completed/${userId}/episodes/${episodeId}`);

    ref
      .set(true)
      .then(() => resolve())
      .catch(() => reject("Ocurrió un error."));
  });

const subscribeToProfileDetails = (userId, onReceive, onDelete) => {
  const ref = database.ref(`users/${userId}`);

  const handler = (snapshot) => onReceive(snapshot.key, snapshot.val());

  const handlerOnDelete = (snapshot) => onDelete(snapshot.key);

  ref.on("child_added", handler);
  ref.on("child_changed", handler);
  ref.on("child_removed", handlerOnDelete);

  return () => {
    ref.off("child_added", handler);
    ref.off("child_changed", handler);
    ref.off("child_removed", handlerOnDelete);
  };
};

const updateProfile = ({ name, lastname, phone, passwordNew, password }) =>
  new Promise((resolve, reject) => {
    const currentUser = firebase.auth().currentUser;
    const userId = currentUser?.uid;
    if (!userId) {
      reject("No has iniciado sesión.");
      return;
    }

    let updates = {};

    updates[`users/${userId}/name`] = name;
    updates[`users/${userId}/lastname`] = lastname;
    updates[`users/${userId}/phone`] = phone;

    database.ref().update(updates, (error) => {
      if (error) reject("Ocurrió un error.");
      else if (passwordNew !== "") {
        const credential = firebase.auth.EmailAuthProvider.credential(
          currentUser.email,
          password
        );

        currentUser
          .reauthenticateWithCredential(credential)
          .then(() => {
            currentUser
              .updatePassword(passwordNew)
              .then(() => resolve())
              .catch((error) => {
                let notification = "";
                switch (error.code) {
                  case "auth/weak-password":
                    notification =
                      "La nueva contraseña no es suficientemente segura.";
                    break;
                  default:
                    notification = `Ocurrió un error (${error.code}).`;
                    break;
                }
                reject(notification);
              });
          })
          .catch((error) => {
            let notification = "";
            switch (error.code) {
              case "auth/wrong-password":
                notification = `La actual contraseña es incorrecta.`;
                break;
              default:
                notification = `Ocurrió un error (${error.code}).`;
                break;
            }
            reject(notification);
          });
      } else resolve();
    });
  });

const activate = (code) =>
  new Promise(async (resolve, reject) => {
    const userId = getUserId();
    if (!userId) {
      reject("No has iniciado sesión.");
      return;
    }

    let email = firebase.auth().currentUser.email;
    if (email === null) email = userId.slice(0, 5);

    try {
      const {data} = await ActivateCode({
        userId: userId,
        email: email,
        code: code
      });

      const seed = data?.seed;
      if (seed !== undefined) {
        try {
          localStorage.setItem(userId, seed);
        } catch (error) {
          
        }
      }

      resolve();
    } catch (error) {
      reject("Ocurrió un error. Inténtalo nuevamente.");
    }
  });

const registerSeed = () =>
  new Promise((resolve, reject) => {
    const userId = getUserId();
    if (!userId) reject("No has iniciado sesión.");

    const seed = uuid();
    localStorage.setItem(userId, seed);
    const ref = database.ref(`users/${userId}/seed`);
    ref
      .set(seed)
      .then(() => resolve(seed))
      .catch(() => {
        localStorage.removeItem(userId);
        reject("Ocurrió un error.");
      });
  });

const subscribeToOrderId = (orderId, onReceive) => {
  const ref = database.ref(`order_data/${orderId}`);

  const handler = (snapshot) => {
    onReceive(snapshot.exists() ? snapshot.val() : {});
  };

  ref.on("value", handler);

  return () => ref.off("value", handler);
};

const subscribeToLastNotifications = (
  onReceive,
  onDelete,
  userId = getUserId()
) => {
  if (!userId) return () => {};

  let ref = database.ref(`notifications/${userId}`).orderByKey();

  ref = ref.limitToLast(10);

  const handler = (snapshot) =>
    snapshot.exists()
      ? onReceive({ id: snapshot.key, ...snapshot.val() })
      : onDelete(snapshot.key);

  const handlerOnDelete = (snapshot) => onDelete(snapshot.key);

  ref.on("child_added", handler);
  ref.on("child_changed", handler);
  ref.on("child_removed", handlerOnDelete);

  return () => {
    ref.off("child_added", handler);
    ref.off("child_changed", handler);
    ref.off("child_removed", handlerOnDelete);
  };
};

const completeNotification = (notificationId) =>
  new Promise((resolve, reject) => {
    const userId = getUserId();
    if (!userId) reject("No has iniciado sesión.");

    const ref = database.ref(`notifications/${userId}/${notificationId}/state`);
    ref
      .set("VIEWED")
      .then(() => resolve())
      .catch(() => {
        reject("Ocurrió un error.");
      });
  });

export default {
  onAuthStateChanged,
  logout,
  login,
  register,
  sendPasswordReset,
  subscribeToOrderId,
  subscribeToCompletedEpisodes,
  subscribeToCompletedChapters,
  subscribeToCompletedLessons,
  subscribeToPurchases,
  subscribeToAttemptDetails,
  sendAttempt,
  makeLessonCompleted,
  makeChapterCompleted,
  makeEpisodeCompleted,
  subscribeToProfileDetails,
  updateProfile,
  activate,
  registerSeed,
  subscribeToLastNotifications,
  completeNotification,
};
